// Detail view for a single billing document (proforma / invoice / credit).
// Shows full header, parties, line items and totals, plus a Download PDF
// button that streams the server-rendered mpdf output as a blob.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t, getLocale } from '../core/i18n.js';
import { formatDate, formatMoney } from '../core/format.js';

export class DocumentView extends Component {
  constructor(ctx) {
    super();
    this.id = (ctx && ctx.params && ctx.params.id) || '';
    this.state = { doc: null, error: null, downloading: false };
  }

  template() {
    if (this.state.error && !this.state.doc) {
      return `<section class="stack"><div class="error-banner">${esc(this.state.error)}</div></section>`;
    }
    const d = this.state.doc;
    if (!d) return `<section class="stack"><div class="muted">${esc(t('document.loading'))}</div></section>`;

    const c = d.tenancy || {};
    const currency = d.currency || c.currency || 'HUF';
    const open = (d.total_amount || 0) - (d.paid_amount || 0);
    const titleKey = d.type === 'INVOICE' ? 'document.title_invoice'
      : d.type === 'CREDIT' ? 'document.title_credit'
      : 'document.title_proforma';
    const backHref = c.id ? `#/tenancies/${encodeURIComponent(c.id)}?tab=billing` : '#/tenancies';

    return `
      <section class="stack">
        <a href="${backHref}" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('document.back'))}</span></a>

        <div class="card stack">
          <div class="row-between" style="align-items:flex-start;gap:var(--space-3);flex-wrap:wrap">
            <div class="stack-2">
              <span class="faint">${esc(t(titleKey))}</span>
              <h2 style="margin:0">${esc(t('document.number_label'))} ${esc(d.number || '')}</h2>
              <span class="badge ${this._badgeClass(d.status)}">${esc(d.status || '')}</span>
            </div>
            <button class="btn btn-primary" data-action="pdf" ${this.state.downloading ? 'disabled' : ''}>
              ${icon('file-text', { size: 'sm' })}
              <span>${esc(this.state.downloading ? t('document.download_in_progress') : t('document.download_pdf'))}</span>
            </button>
          </div>
          ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
          <div class="row" style="gap:var(--space-4);flex-wrap:wrap">
            <div class="stack-2"><span class="faint">${esc(t('document.issue_date'))}</span><strong>${esc(formatDate(d.issue_date))}</strong></div>
            <div class="stack-2"><span class="faint">${esc(t('document.due_date'))}</span><strong>${esc(formatDate(d.due_date))}</strong></div>
          </div>
        </div>

        ${this._partiesHtml(c)}
        ${this._propertyHtml(c)}
        ${this._itemsHtml(d, currency)}
        ${this._totalsHtml(d, currency, open)}
        ${d.notes ? `<div class="card stack-2"><strong>${esc(t('document.notes'))}</strong><span class="muted">${esc(d.notes)}</span></div>` : ''}
      </section>
    `;
  }

  mount() {
    if (this.state.doc === null && !this.state.error) {
      this._load();
      return;
    }
    const btn = this.el && this.el.querySelector('[data-action="pdf"]');
    if (btn) btn.addEventListener('click', () => this._downloadPdf());
  }

  _partiesHtml(c) {
    const issuerName = c.landlord_name || '—';
    const issuerEmail = c.landlord_email || '';
    const issuerAddress = c.landlord_billing_address || '';
    const issuerTax = c.landlord_billing_tax_number || '';
    const issuerBank = c.landlord_billing_bank_account || '';
    const recipientName = c.tenant_name || c.invitation_name || '—';
    const recipientEmail = c.tenant_email || c.invitation_email || '';
    const issuerExtra = (label, value) => value
      ? `<span class="muted"><span class="faint">${esc(label)}:</span> ${esc(value)}</span>`
      : '';
    return `
      <div class="row" style="gap:var(--space-3);flex-wrap:wrap">
        <div class="card stack-2 grow" style="min-width:240px">
          <span class="faint">${esc(t('document.issuer'))}</span>
          <strong>${esc(issuerName)}</strong>
          ${issuerEmail ? `<span class="muted">${esc(issuerEmail)}</span>` : ''}
          ${issuerExtra(t('document.issuer_address'), issuerAddress)}
          ${issuerExtra(t('document.issuer_tax_number'), issuerTax)}
          ${issuerExtra(t('document.issuer_bank_account'), issuerBank)}
        </div>
        <div class="card stack-2 grow" style="min-width:240px">
          <span class="faint">${esc(t('document.recipient'))}</span>
          <strong>${esc(recipientName)}</strong>
          ${recipientEmail ? `<span class="muted">${esc(recipientEmail)}</span>` : ''}
        </div>
      </div>
    `;
  }

  _propertyHtml(c) {
    const units = Array.isArray(c.units) ? c.units : [];
    if (units.length === 0) return '';
    const u = units[0];
    const addr = [u.property_name, u.address_line1, u.city].filter(Boolean).join(' · ');
    const idents = units.map((x) => `${x.type || ''} ${x.identifier || ''}`.trim()).filter(Boolean).join(', ');
    return `
      <div class="card stack-2">
        <span class="faint">${esc(t('document.property'))}</span>
        <strong>${esc(addr || '—')}</strong>
        ${idents ? `<span class="muted">${esc(idents)}</span>` : ''}
      </div>
    `;
  }

  _itemsHtml(d, currency) {
    const items = Array.isArray(d.items) ? d.items : [];
    return `
      <div class="card stack">
        <h3 style="margin:0">${esc(t('document.items_heading'))}</h3>
        ${items.length === 0
          ? `<div class="empty">${esc(t('document.no_items'))}</div>`
          : `<div class="stack-2">${items.map((it) => this._itemRow(it, currency)).join('')}</div>`}
      </div>
    `;
  }

  _itemRow(it, currency) {
    const qty = it.quantity != null && it.quantity !== '' ? String(it.quantity).replace(/0+$/,'').replace(/\.$/,'') : '';
    const uom = it.meter_unit_of_measure || '';
    const unitPrice = it.unit_price != null
      ? `${formatMoney(it.unit_price, currency)}${uom ? ' / ' + esc(uom) : ''}` : '';
    const base = Number(it.base_fee || 0);
    const vatAmount = Number(it.vat_amount || 0);
    const gross = it.gross_amount != null ? Number(it.gross_amount) : Number(it.amount || 0);
    const breakdown = [
      base > 0 ? `${esc(t('document.base_fee'))}: ${formatMoney(base, currency)}` : null,
      qty ? `${esc(qty)}${uom ? ' ' + esc(uom) : ''}${unitPrice ? ' \u00d7 ' + unitPrice : ''}` : null,
    ].filter(Boolean).join(' + ');
    const vatLine = vatAmount > 0
      ? `${esc(t('document.net'))}: ${formatMoney(it.amount, currency)} \u00b7 ${esc(t('document.vat'))} ${Number(it.vat_rate) / 100}%: ${formatMoney(vatAmount, currency)}`
      : '';
    return `
      <div class="row-between" style="gap:var(--space-3);align-items:flex-start">
        <div class="stack-2 grow">
          <strong>${esc(it.description || '')}</strong>
          ${breakdown ? `<span class="faint">${breakdown}</span>` : ''}
          ${vatLine ? `<span class="faint">${vatLine}</span>` : ''}
        </div>
        <strong>${esc(formatMoney(gross, currency))}</strong>
      </div>
    `;
  }

  _totalsHtml(d, currency, open) {
    const items = Array.isArray(d.items) ? d.items : [];
    const vatTotal = items.reduce((sum, it) => sum + Number(it.vat_amount || 0), 0);
    const netTotal = items.reduce((sum, it) => sum + Number(it.amount || 0), 0);
    return `
      <div class="card stack-2">
        ${vatTotal > 0 ? `
        <div class="row-between"><span class="faint">${esc(t('document.net_total'))}</span><span>${esc(formatMoney(netTotal, currency))}</span></div>
        <div class="row-between"><span class="faint">${esc(t('document.vat_total'))}</span><span>${esc(formatMoney(vatTotal, currency))}</span></div>` : ''}
        <div class="row-between"><span class="faint">${esc(t('document.total'))}</span><strong>${esc(formatMoney(d.total_amount, currency))}</strong></div>
        <div class="row-between"><span class="faint">${esc(t('document.paid'))}</span><span>${esc(formatMoney(d.paid_amount || 0, currency))}</span></div>
        ${open > 0 && d.type === 'PROFORMA' ? `<div class="row-between"><strong>${esc(t('document.open'))}</strong><strong>${esc(formatMoney(open, currency))}</strong></div>` : ''}
      </div>
    `;
  }

  _badgeClass(status) {
    if (status === 'PAID') return 'badge-success';
    if (status === 'CANCELLED') return 'badge-muted';
    return 'badge-warning';
  }

  async _load() {
    try {
      const doc = await api.get(`/documents/${encodeURIComponent(this.id)}`);
      this.setState({ doc, error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('document.errors.load') });
    }
  }

  async _downloadPdf() {
    if (this.state.downloading) return;
    this.setState({ downloading: true, error: null });
    try {
      const blob = await api.get(
        `/documents/${encodeURIComponent(this.id)}/pdf?lang=${encodeURIComponent(getLocale())}`,
        { responseType: 'blob' }
      );
      const number = (this.state.doc && this.state.doc.number) || this.id;
      const safeName = String(number).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'document';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('document.errors.download') });
    } finally {
      this.setState({ downloading: false });
    }
  }
}
